import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useEffect } from 'react';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import { GridItem, GridSection } from 'src/components/CustomModal';
import { ReferralService } from 'src/crs/referral/services/ReferralService';
import useObjectState from 'src/hooks/useObjectState';

interface WorkflowFormProps {
  serviceRequest: WrappedServiceRequest | null;
  show: boolean;
  i18n: i18nFunction;
  updateState: Function;
  error: boolean;
}

export default function WorkflowForm({
  serviceRequest,
  show,
  i18n,
  updateState,
  error,
}: WorkflowFormProps) {
  const [{ workflows, workflowScope, loading }, updateStateForm] = useObjectState<{
    workflows: any;
    workflowScope: any;
    loading: boolean;
  }>({
    workflows: null,
    workflowScope: null,
    loading: false,
  });

  useEffect(() => {
    if (!show) return;

    let isMounted = true;

    const loadWorkflows = async () => {
      updateStateForm({ loading: true });

      try {
        const workflowsByType = await WorkflowService.getWorkflowByType('referral');
        const onlyActive = workflowsByType?.filter((item: any) => item?.status === 'Active');
        let selectedWorkflowScope = onlyActive?.length === 1 ? onlyActive[0] : null;

        if (serviceRequest?.id) {
          const workflowBox = await ReferralService.getOne(serviceRequest.id);
          selectedWorkflowScope =
            onlyActive?.find((e: any) => e?.code === workflowBox?.workflowScope) || null;
          updateState({ workflowScope: selectedWorkflowScope });
        }

        if (!isMounted) return;

        updateStateForm({
          workflows: onlyActive,
          workflowScope: selectedWorkflowScope,
        });
      } catch (error) {
        console.error('WorkflowForm load error:', error);
      } finally {
        if (isMounted) {
          updateStateForm({ loading: false });
        }
      }
    };

    loadWorkflows();

    return () => {
      isMounted = false;
    };
  }, [show, serviceRequest?.id, updateState, updateStateForm]);

  return (
    <GridSection>
      <GridItem xs={12}>
        <Autocomplete
          disabled={loading || Boolean(serviceRequest?.id)}
          value={workflowScope}
          fullWidth
          onChange={(_: any, value: any | null) => {
            updateStateForm({
              workflowScope: value,
            });
            updateState((state) => ({
              ...(value && { error: { ...(state?.error ?? {}), workflowScope: null } }),
              workflowScope: value,
            }));
          }}
          options={workflows}
          getOptionLabel={({ name }: any) => name}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${i18n('referral.details.workflowScope', 'crs')}*`}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              error={!!error}
              helperText={!!error && i18n('errorMessages.fieldIsRequired', 'crs')}
            />
          )}
        />
      </GridItem>
    </GridSection>
  );
}
